/**
 * v8.9.12 (Feb 2026) — Shared manual-session hook.
 *
 * Wraps the /api/manual-session/* endpoints so every manual product
 * (Matches/Differs, Rise/Fall, Digit OVER 4) reads from a single
 * authoritative session state and pushes settlement events to the same
 * backend row.
 *
 * Behaviour:
 *   • Auto-fetches on mount + every 10s.
 *   • `start(balance)` initializes the day's session with start_balance
 *     (idempotent — same UTC day = no-op if already started).
 *   • `recordTrade(profit, product)` posts a settled trade. Returns the
 *     refreshed session view immediately so the caller can drive a
 *     just-landed toast.
 *   • Auto-resets at UTC midnight via 60s poller (server already
 *     partitions by utc_day_key, but the client refreshes on its own).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  account_id: null,
  day: null,
  start_balance: 0,
  session_pnl: 0,
  trades: 0,
  wins: 0,
  losses: 0,
  target_profit: 0,
  max_loss: 0,
  progress_pct: 0,
  loss_pct: 0,
  status: "ACTIVE",
  started_at: null,
  target_pct_setting: 20,
  max_loss_pct_setting: 10,
};

export function useManualSession(accountId) {
  const [session, setSession] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await axios.get(`${API}/manual-session/${accountId}`);
      setSession(res.data || EMPTY);
    } catch (e) {
      // Silent — UI defaults to the last known state.
    }
  }, [accountId]);

  const start = useCallback(
    async (balance, opts = {}) => {
      if (!accountId || !balance || balance <= 0) return null;
      setLoading(true);
      try {
        const res = await axios.post(`${API}/manual-session/${accountId}/start`, {
          balance: Number(balance),
          target_pct: opts.target_pct != null ? Number(opts.target_pct) : null,
          max_loss_pct: opts.max_loss_pct != null ? Number(opts.max_loss_pct) : null,
        });
        setSession(res.data);
        return res.data;
      } finally {
        setLoading(false);
      }
    },
    [accountId]
  );

  const recordTrade = useCallback(
    async (profit, product) => {
      if (!accountId || profit == null) return null;
      try {
        const res = await axios.post(`${API}/manual-session/${accountId}/trade`, {
          profit: Number(profit),
          product: product || null,
        });
        setSession(res.data);
        return res.data;
      } catch (e) {
        return null;
      }
    },
    [accountId]
  );

  const reset = useCallback(async () => {
    if (!accountId) return;
    await axios.post(`${API}/manual-session/${accountId}/reset`);
    setSession({ ...EMPTY, account_id: accountId });
  }, [accountId]);

  // v8.9.12m — Edit TP/SL on an existing session without resetting.
  const updateTargets = useCallback(
    async ({ target_pct, max_loss_pct }) => {
      if (!accountId) return null;
      try {
        const res = await axios.post(`${API}/manual-session/${accountId}/targets`, {
          target_pct: target_pct != null ? Number(target_pct) : null,
          max_loss_pct: max_loss_pct != null ? Number(max_loss_pct) : null,
        });
        setSession(res.data);
        return res.data;
      } catch (e) {
        return null;
      }
    },
    [accountId]
  );

  useEffect(() => {
    refresh();
    pollRef.current = setInterval(refresh, 10000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refresh]);

  return { session, loading, refresh, start, recordTrade, reset, updateTargets };
}

export default useManualSession;
